// src/features/documents/components/DocumentBuilder/PrintableDocument.jsx
import React, { forwardRef, useState, useRef, useCallback } from 'react';
import p from './PrintableDocument.module.css';

/* ══ helpers ══ */
const fmtDate = v => {
  if (!v) return '—';
  try { const [y,m,d]=v.split('-'); return `${d}/${m}/${y}`; }
  catch { return v; }
};

let _uid = 20;
const uid = () => `c${++_uid}`;

/* أنواع الأعمدة */
const COL_TYPE = { TEXT:'text', NUM:'num', CALC:'calc' };

const OPS = [
  { value:'×', label:'× ضرب'   },
  { value:'÷', label:'÷ قسمة'  },
  { value:'+', label:'+ جمع'   },
  { value:'−', label:'− طرح'   },
];

const INIT_COLS = [
  { id:'c1', label:'الوصف',       width:40, type:COL_TYPE.TEXT },
  { id:'c2', label:'الكمية',      width:14, type:COL_TYPE.NUM  },
  { id:'c3', label:'سعر الوحدة', width:24, type:COL_TYPE.NUM  },
  { id:'c4', label:'الإجمالي',   width:22, type:COL_TYPE.CALC,
    calc:{ colA:'c2', op:'×', colB:'c3' } },
];

const INIT_ROWS = [
  { c1:'تصوير حفل التخرج',     c2:'1', c3:'300,000', c4:'' },
  { c1:'تعديل ومونتاج الصور',  c2:'1', c3:'100,000', c4:'' },
  { c1:'ألبوم طباعة A4',        c2:'2', c3:'25,000',  c4:'' },
];

/* ── تحويل قيمة لرقم ── */
const toNum = v => parseFloat(String(v||'').replace(/,/g,'').trim()) || 0;

/* ── حساب خلية محسوبة ── */
const calcCell = (row, col, allCols) => {
  if (col.type !== COL_TYPE.CALC || !col.calc) return null;
  const { colA, op, colB } = col.calc;
  // تحقق أن المصدرين رقميين أو محسوبين
  const srcA = allCols.find(c=>c.id===colA);
  const srcB = allCols.find(c=>c.id===colB);
  if (!srcA || !srcB) return null;

  const getVal = (srcCol, r) => {
    if (srcCol.type === COL_TYPE.CALC) {
      const res = calcCell(r, srcCol, allCols);
      return res !== null ? toNum(res) : 0;
    }
    return toNum(r[srcCol.id]);
  };

  const a = getVal(srcA, row);
  const b = getVal(srcB, row);

  let result;
  switch(op) {
    case '×': result = a * b; break;
    case '÷': result = b !== 0 ? a / b : 0; break;
    case '+': result = a + b; break;
    case '−': result = a - b; break;
    default:  result = 0;
  }
  // إذا كانت القيم صفر ولا يوجد مدخلات — لا تعرض شيئاً
  if (a === 0 && b === 0) return '';
  return Number.isInteger(result)
    ? result.toLocaleString('en-US')
    : parseFloat(result.toFixed(4)).toLocaleString('en-US');
};

/* ── جمع عمود (رقمي أو محسوب) ── */
const sumCol = (rows, col, allCols) => {
  let total = 0, hasAny = false;
  rows.forEach(r => {
    let raw;
    if (col.type === COL_TYPE.CALC) {
      raw = calcCell(r, col, allCols);
    } else {
      raw = r[col.id];
    }
    const n = toNum(raw);
    if (raw !== '' && raw !== null && raw !== undefined && !isNaN(n)) {
      total += n; hasAny = true;
    }
  });
  return hasAny ? total.toLocaleString('en-US') : '';
};

/* ── توزيع العروض ── */
function redist(cols, idx, newW) {
  const w = Math.min(75, Math.max(5, newW));
  const next = cols.map((c,i) => i===idx ? {...c,width:w} : c);
  const rest = next.filter((_,i) => i!==idx);
  const rSum = rest.reduce((s,c) => s+c.width, 0);
  const tgt  = 100 - w;
  return next.map((c,i) => {
    if (i===idx) return c;
    return {...c, width: rSum>0
      ? Math.max(5,Math.round(tgt*(c.width/rSum)))
      : Math.max(5,Math.floor(tgt/rest.length))};
  });
}

/* ══ خلية قابلة للتحرير ══ */
const Cell = ({ value, onChange, isHead, className, readOnly }) => {
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);
  const start = () => { if(readOnly) return; setEditing(true); setTimeout(()=>ref.current?.select(),0); };
  const done  = v => { setEditing(false); onChange(v); };

  if (editing) return (
    <input
      ref={ref}
      className={`${className} ${p.editing}`}
      defaultValue={value}
      onBlur={e=>done(e.target.value)}
      onKeyDown={e=>{
        if(e.key==='Enter'||e.key==='Tab'){e.preventDefault();done(e.target.value);}
        if(e.key==='Escape') setEditing(false);
      }}
      autoFocus
    />
  );

  return (
    <div
      className={`${className} ${p.cell} ${isHead?p.cellHead:p.cellBody} ${readOnly?p.cellCalc:''}`}
      onClick={start}
      title={readOnly ? 'محسوب تلقائياً' : 'انقر للتعديل'}
    >
      {value||<span className={p.placeholder}>—</span>}
    </div>
  );
};

/* ══ مقبض تغيير العرض ══ */
const Grip = ({ idx, cols, setCols, tableRef }) => {
  const dragging=useRef(false), x0=useRef(0), w0=useRef(0);
  const onDown = useCallback(e => {
    e.preventDefault();
    dragging.current=true; x0.current=e.clientX; w0.current=cols[idx].width;
    const move=ev=>{
      if(!dragging.current) return;
      const tw=tableRef.current?.offsetWidth||730;
      const dw=Math.round(((ev.clientX-x0.current)/tw)*100);
      setCols(prev=>redist(prev,idx,w0.current+dw));
    };
    const up=()=>{ dragging.current=false; window.removeEventListener('mousemove',move); window.removeEventListener('mouseup',up); };
    window.addEventListener('mousemove',move);
    window.addEventListener('mouseup',up);
  },[idx,cols,setCols,tableRef]);
  return <div className={p.grip} onMouseDown={onDown} title={`${cols[idx]?.width}%`}/>;
};

/* ══ بوب-أب إعداد العمود المحسوب ══ */
const CalcPopup = ({ col, cols, onSave, onClose }) => {
  const eligible = cols.filter(c => c.id!==col.id && c.type!==COL_TYPE.TEXT);
  const [colA, setColA] = useState(col.calc?.colA || eligible[0]?.id || '');
  const [op,   setOp]   = useState(col.calc?.op   || '×');
  const [colB, setColB] = useState(col.calc?.colB || eligible[1]?.id || eligible[0]?.id || '');

  const colName = id => cols.find(c=>c.id===id)?.label || id;

  return (
    <div className={p.popupOverlay} onClick={onClose}>
      <div className={p.popup} onClick={e=>e.stopPropagation()}>
        <div className={p.popupHead}>
          <span className={p.popupTitle}>⚙ إعداد العمود المحسوب</span>
          <button className={p.popupClose} onClick={onClose}>×</button>
        </div>

        {eligible.length < 2 ? (
          <p className={p.popupWarn}>
            ⚠ يجب أن يكون هناك عمودان رقميان على الأقل قبل إضافة عمود محسوب.
          </p>
        ) : (
          <>
            <div className={p.popupBody}>
              <div className={p.popupFormula}>
                {/* عمود A */}
                <select className={p.popupSel} value={colA} onChange={e=>setColA(e.target.value)}>
                  {eligible.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                {/* العملية */}
                <select className={`${p.popupSel} ${p.popupOp}`} value={op} onChange={e=>setOp(e.target.value)}>
                  {OPS.map(o=><option key={o.value} value={o.value}>{o.value}</option>)}
                </select>
                {/* عمود B */}
                <select className={p.popupSel} value={colB} onChange={e=>setColB(e.target.value)}>
                  {eligible.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className={p.popupPreview}>
                النتيجة = <strong>{colName(colA)}</strong> {op} <strong>{colName(colB)}</strong>
              </div>
            </div>
            <div className={p.popupFoot}>
              <button className={p.popupCancel} onClick={onClose}>إلغاء</button>
              <button
                className={p.popupSave}
                onClick={()=>{ onSave({colA,op,colB}); onClose(); }}
                disabled={!colA||!colB}
              >
                حفظ
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ══ الورقة الرئيسية ══ */
const PrintableDocument = forwardRef(({
  settings, docTitle, docNum, docDate, dueDate, party1, phone, notes, sigs,
}, ref) => {

  const [cols,       setCols]       = useState(INIT_COLS);
  const [rows,       setRows]       = useState(INIT_ROWS);
  const [showTotals, setShowTotals] = useState(true);
  const [editingCalc, setEditingCalc] = useState(null); // id العمود الذي نعدّل حسابه
  const tableRef = useRef(null);

  /* ── دورة أنواع العمود: نص → رقم → محسوب → نص ── */
  const cycleType = id => {
    setCols(prev => prev.map(c => {
      if (c.id !== id) return c;
      if (c.type === COL_TYPE.TEXT) return {...c, type:COL_TYPE.NUM,  calc:undefined};
      if (c.type === COL_TYPE.NUM)  return {...c, type:COL_TYPE.CALC, calc:undefined};
      /* CALC → TEXT */                return {...c, type:COL_TYPE.TEXT, calc:undefined};
    }));
    // إذا أصبح محسوباً افتح البوب-أب
    const col = cols.find(c=>c.id===id);
    if (col?.type === COL_TYPE.NUM) setEditingCalc(id);
  };

  const saveCalc = (id, calcDef) =>
    setCols(prev=>prev.map(c=>c.id===id?{...c,calc:calcDef}:c));

  /* ── أعمدة ── */
  const addCol = () => {
    const id=uid(), give=14;
    setCols(prev=>{
      const next=prev.map((c,i)=>i===prev.length-1?{...c,width:Math.max(5,c.width-give)}:c);
      next.push({id,label:'عمود جديد',width:give,type:COL_TYPE.TEXT});
      return next;
    });
    setRows(prev=>prev.map(r=>({...r,[id]:''})));
  };

  const delCol = id => {
    if(cols.length<=1) return;
    const idx=cols.findIndex(c=>c.id===id);
    const w=cols[idx].width;
    const rem=cols.filter(c=>c.id!==id);
    rem[rem.length-1]={...rem[rem.length-1],width:Math.min(90,rem[rem.length-1].width+w)};
    // أزل أي أعمدة محسوبة تعتمد على هذا العمود
    const cleaned = rem.map(c =>
      c.type===COL_TYPE.CALC && (c.calc?.colA===id||c.calc?.colB===id)
        ? {...c, calc:undefined}
        : c
    );
    setCols(cleaned);
    setRows(prev=>prev.map(r=>{ const nr={...r}; delete nr[id]; return nr; }));
  };

  const setColLabel  = (id,v) => setCols(prev=>prev.map(c=>c.id===id?{...c,label:v}:c));

  /* ── صفوف ── */
  const addRow  = ()         => { const r={}; cols.forEach(c=>r[c.id]=''); setRows(prev=>[...prev,r]); };
  const delRow  = i          => setRows(prev=>prev.filter((_,x)=>x!==i));
  const setCell = (ri,id,v)  => setRows(prev=>prev.map((r,i)=>i===ri?{...r,[id]:v}:r));

  /* ── أيقونة نوع العمود ── */
  const typeIcon  = t => t===COL_TYPE.NUM?'#':t===COL_TYPE.CALC?'ƒ':'أ';
  const typeTitle = t => t===COL_TYPE.NUM?'رقمي — انقر للتحويل لمحسوب':t===COL_TYPE.CALC?'محسوب — انقر للتحويل لنصي':'نصي — انقر للتحويل لرقمي';
  const typeCls   = t => t===COL_TYPE.CALC?p.typeBtnCalc:t===COL_TYPE.NUM?p.typeBtnOn:'';

  return (
    <div ref={ref} className={p.page}>

      {/* بوب-أب العمود المحسوب */}
      {editingCalc && (
        <CalcPopup
          col={cols.find(c=>c.id===editingCalc)}
          cols={cols}
          onSave={def=>saveCalc(editingCalc,def)}
          onClose={()=>setEditingCalc(null)}
        />
      )}

      {/* watermark — شعار الشركة خلفية شفافة */}
      {settings?.logo && (
        <div className={p.watermark} aria-hidden="true">
          <img src={settings.logo} alt="" className={p.watermarkImg}/>
        </div>
      )}

      {/* بانر */}
      {settings?.banner
        ? <img src={settings.banner} alt="بانر" className={p.banner}/>
        : <div className={p.bannerFb}>
            <span className={p.fbName}>{settings?.company_name||'اسم الشركة'}</span>
            <div className={p.fbRight}>
              {settings?.phone_number&&<span>{settings.phone_number}</span>}
              {settings?.address&&<span>{settings.address}</span>}
            </div>
          </div>
      }

      {/* بيانات الرأس */}
      <div className={p.meta}>
        <div className={p.metaCell}><span className={p.metaK}>العميل / الطرف الثاني</span><span className={p.metaV}>{party1||'—'}</span></div>
        <div className={p.metaCell}><span className={p.metaK}>رقم الهاتف</span><span className={p.metaV} style={{direction:'ltr',textAlign:'right'}}>{phone||'—'}</span></div>
        <div className={p.metaCell}><span className={p.metaK}>رقم المستند</span><span className={p.metaV}>{docNum||'—'}</span></div>
        <div className={p.metaCell}><span className={p.metaK}>تاريخ الإصدار</span><span className={p.metaV}>{fmtDate(docDate)}</span></div>
        {dueDate&&<div className={p.metaCell} style={{gridColumn:'1/-1'}}><span className={p.metaK}>تاريخ الاستحقاق</span><span className={p.metaV}>{fmtDate(dueDate)}</span></div>}
      </div>

      {/* شريط أدوات */}
      <div className={p.toolbar}>
        <span className={p.toolHint}>
          <span className={p.typeLegend}><span className={p.legendA}>أ</span>نص</span>
          <span className={p.typeLegend}><span className={p.legendN}>#</span>رقم</span>
          <span className={p.typeLegend}><span className={p.legendF}>ƒ</span>محسوب</span>
          <span className={p.sep}>·</span>
          انقر الأيقونة للتبديل بين الأنواع
        </span>
        <button
          className={`${p.totalBtn} ${showTotals?p.totalBtnOn:''}`}
          onClick={()=>setShowTotals(v=>!v)}
        >
          {showTotals ? '∑ إخفاء الإجمالي' : '∑ إظهار الإجمالي'}
        </button>
      </div>

      {/* عنوان */}
      <div className={p.titleRow}>
        <h1 className={p.title}>{docTitle||'عنوان المستند'}</h1>
      </div>

      {/* الجدول */}
      <div className={p.tblWrap}>
        <table className={p.tbl} ref={tableRef}>
          <colgroup>
            {cols.map(c=><col key={c.id} style={{width:`${c.width}%`}}/>)}
            <col className={p.actionCol}/>
          </colgroup>

          <thead className={p.thead}>
            <tr>
              {cols.map((col,ci)=>(
                <th key={col.id} className={`${p.th} ${col.type===COL_TYPE.CALC?p.thCalc:''}`}>
                  <div className={p.thInner}>
                    <Cell value={col.label} onChange={v=>setColLabel(col.id,v)} isHead className={p.thCell}/>
                    {/* زر نوع العمود */}
                    <button
                      className={`${p.typeBtn} ${typeCls(col.type)}`}
                      onClick={()=>{
                        cycleType(col.id);
                        // إذا كان سيصبح محسوباً افتح البوب-أب
                        if(col.type===COL_TYPE.NUM) setTimeout(()=>setEditingCalc(col.id),50);
                      }}
                      title={typeTitle(col.type)}
                    >{typeIcon(col.type)}</button>
                    {/* زر إعداد الحساب (فقط للمحسوب) */}
                    {col.type===COL_TYPE.CALC&&(
                      <button
                        className={p.calcSetBtn}
                        onClick={()=>setEditingCalc(col.id)}
                        title="تعديل معادلة الحساب"
                      >⚙</button>
                    )}
                    {ci<cols.length-1&&<Grip idx={ci} cols={cols} setCols={setCols} tableRef={tableRef}/>}
                    {cols.length>1&&<button className={p.colDel} onClick={()=>delCol(col.id)}>×</button>}
                  </div>
                  {/* معادلة مصغّرة أسفل العنوان */}
                  {col.type===COL_TYPE.CALC&&col.calc&&(
                    <div className={p.calcFormula}>
                      {cols.find(c=>c.id===col.calc.colA)?.label||'؟'}
                      {' '}{col.calc.op}{' '}
                      {cols.find(c=>c.id===col.calc.colB)?.label||'؟'}
                    </div>
                  )}
                </th>
              ))}
              <th className={p.addColTh}>
                <button className={p.addColBtn} onClick={addCol}>＋</button>
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row,ri)=>(
              <tr key={ri} className={ri%2===1?p.alt:''}>
                {cols.map(col=>{
                  const isCalc = col.type===COL_TYPE.CALC;
                  const val = isCalc ? (calcCell(row,col,cols)||'') : (row[col.id]??'');
                  return (
                    <td key={col.id} className={`${p.td} ${col.type!==COL_TYPE.TEXT?p.tdNum:''} ${isCalc?p.tdCalc:''}`}>
                      <Cell
                        value={val}
                        onChange={v=>setCell(ri,col.id,v)}
                        className={p.tdCell}
                        readOnly={isCalc}
                      />
                    </td>
                  );
                })}
                <td className={p.actionTd}>
                  <button className={p.rowDel} onClick={()=>delRow(ri)}>×</button>
                </td>
              </tr>
            ))}

            {/* صف الإجمالي */}
            {showTotals&&(
              <tr className={p.totalRow}>
                {cols.map((col,ci)=>{
                  const showSum = col.type===COL_TYPE.NUM||col.type===COL_TYPE.CALC;
                  const total   = showSum ? sumCol(rows,col,cols) : '';
                  return (
                    <td key={col.id} className={`${p.totalCell} ${showSum?p.totalCellNum:''}`}>
                      {ci===0
                        ? <span className={p.totalLabel}>الإجمالي الكلي</span>
                        : total ? <span className={p.totalVal}>{total}</span> : null
                      }
                    </td>
                  );
                })}
                <td className={p.actionTd}/>
              </tr>
            )}

            {/* سطر إضافة */}
            <tr><td colSpan={cols.length+1} className={p.addRowTd}>
              <button className={p.addRowBtn} onClick={addRow}>＋ إضافة سطر</button>
            </td></tr>
          </tbody>
        </table>
      </div>

      {notes&&<div className={p.notes}><strong>ملاحظات: </strong>{notes}</div>}

      {sigs.length>0&&(
        <div className={p.sigs} style={{gridTemplateColumns:`repeat(${Math.min(sigs.length,3)},1fr)`}}>
          {sigs.map((s,i)=>(
            <div key={i} className={p.sig}>
              <div className={p.sigLine}/>
              <p className={p.sigLabel}>{s}</p>
            </div>
          ))}
        </div>
      )}

      <footer className={p.footer}>
        {settings?.company_name&&<span>{settings.company_name}</span>}
        {settings?.phone_number&&<span>{settings.phone_number}</span>}
        {settings?.address&&<span>{settings.address}</span>}
        <span>تاريخ الإصدار: {fmtDate(docDate)}</span>
      </footer>

    </div>
  );
});

PrintableDocument.displayName = 'PrintableDocument';
export default PrintableDocument;